import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPublicAlbums } from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Image, X, ChevronLeft, ChevronRight, Share2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function formatAlbumDate(dateValue) {
  if (!dateValue) return '';
  const [year, month, day] = String(dateValue).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';

  return format(new Date(year, month - 1, day), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
}

export default function Galeria() {
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const { data: albums = [], isLoading } = useQuery({
    queryKey: ['albums'],
    queryFn: () => listPublicAlbums(50)
  });

  const openPhotoViewer = (album, index) => {
    setSelectedAlbum(album);
    setCurrentPhotoIndex(index);
    setSelectedPhoto(album.photos[index]);
  };

  const closePhotoViewer = () => {
    setSelectedPhoto(null);
    setSelectedAlbum(null);
    setCurrentPhotoIndex(0);
  };

  const goToPrevious = () => {
    if (!selectedAlbum) return;
    const newIndex = (currentPhotoIndex - 1 + selectedAlbum.photos.length) % selectedAlbum.photos.length;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(selectedAlbum.photos[newIndex]);
  };

  const goToNext = () => {
    if (!selectedAlbum) return;
    const newIndex = (currentPhotoIndex + 1) % selectedAlbum.photos.length;
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(selectedAlbum.photos[newIndex]);
  };

  const handleShare = async () => {
    if (selectedPhoto && navigator.share) {
      try {
        await navigator.share({
          title: selectedAlbum.name,
          url: selectedPhoto.url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  const openExternalDownload = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Galeria" subtitle="Fotos e momentos especiais" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {albums.length === 0 ? (
          <EmptyState
            icon={Image}
            title="Nenhum álbum disponível"
            description="Em breve teremos fotos para compartilhar."
          />
        ) : (
          <div className="space-y-6">
            {albums.map((album, albumIndex) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: albumIndex * 0.1 }}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-gray-900">{album.name}</h3>
                  {album.event_date && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {formatAlbumDate(album.event_date)}
                    </p>
                  )}
                  {album.description && (
                    <p className="text-sm text-gray-600 mt-2">{album.description}</p>
                  )}
                  {album.download_url && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 min-h-9 h-auto whitespace-normal border-emerald-200 text-left text-emerald-700 hover:bg-emerald-50"
                      onClick={() => openExternalDownload(album.download_url)}
                    >
                      <Download className="w-4 h-4 mr-2 shrink-0" />
                      Acesse o link para visualizar e baixar as fotos
                    </Button>
                  )}
                </div>

                {album.photos && album.photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-0.5 p-0.5">
                    {album.photos.slice(0, 6).map((photo, index) => {
                      const isCover = album.cover_url && photo.url === album.cover_url;

                      return (
                        <div
                          key={index}
                          className="aspect-square relative cursor-pointer overflow-hidden bg-gray-100"
                          onClick={() => openPhotoViewer(album, index)}
                        >
                          <img
                            src={photo.url}
                            alt={photo.caption || `Foto ${index + 1}`}
                            className={`w-full h-full transition-transform duration-300 hover:scale-105 ${isCover ? 'object-contain' : 'object-cover'}`}
                            loading="lazy"
                          />
                          {index === 5 && album.photos.length > 6 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                +{album.photos.length - 6}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {album.photos && album.photos.length > 6 && (
                  <button
                    onClick={() => openPhotoViewer(album, 0)}
                    className="w-full p-3 text-center text-indigo-600 font-medium hover:bg-indigo-50 transition-colors"
                  >
                    Ver todas as {album.photos.length} fotos
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Viewer Modal */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3 pt-[calc(env(safe-area-inset-top)+1rem)] text-white">
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20"
                onClick={closePhotoViewer}
                aria-label="Fechar imagem"
              >
                <X className="w-6 h-6" />
              </Button>
              <span className="text-sm">
                {currentPhotoIndex + 1} / {selectedAlbum?.photos?.length}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20"
                onClick={handleShare}
                aria-label="Compartilhar imagem"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            {/* Image */}
            <div className="flex-1 flex items-center justify-center relative px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="w-full max-w-5xl h-[62vh] sm:h-[74vh] bg-black/30 rounded-xl sm:rounded-2xl overflow-hidden flex items-center justify-center">
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.caption || 'Foto'}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Navigation */}
              {selectedAlbum?.photos?.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-2 sm:left-5 p-2 bg-white/20 rounded-full text-white hover:bg-white/40 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-2 sm:right-5 p-2 bg-white/20 rounded-full text-white hover:bg-white/40 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Caption */}
            {selectedPhoto.caption && (
              <div className="p-4 text-white text-center">
                <p>{selectedPhoto.caption}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
